import React from 'react';
import { Package, Truck, Calendar, Shield, Banknote, Hash, Globe, RotateCcw, ArrowRight } from 'lucide-react';
import { Card } from '../ui/card';
import TransportMap from '../TransportMap';
import { formatContainerType } from '../../utils/formatters';

/**
 * Logistic Block - Carte + bandeau trajet + détails opération
 * Design aligné avec TransferDetail : bandeau vert sous la carte
 */
const LogisticBlock = ({ operation, formatVehicleType, formatOperationType, formatMerchandiseType, formatDate }) => {
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'Non spécifiée';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(amount);
  };

  const stops = operation.isMultiStop && operation.stops?.length > 0
    ? [...operation.stops].sort((a, b) => a.order - b.order)
    : [];
  const hasStops = stops.length > 0;

  /** Truncate city name: first part before comma, max 18 chars */
  const shortName = (fullAddr) => {
    if (!fullAddr) return '?';
    const city = fullAddr.split(',')[0].trim();
    return city.length > 18 ? city.slice(0, 17) + '…' : city;
  };

  const depCity = shortName(operation.departure);
  const arrCity = shortName(operation.arrival);

  return (
    <div className="space-y-3">
      {/* Map + Route Banner (single card, no gap) */}
      <Card className="overflow-hidden">
        <div className="h-[320px] w-full">
          <TransportMap
            departure={operation.departure}
            arrival={operation.arrival}
            stops={hasStops ? operation.stops : []}
          />
        </div>

        {/* Green Route Banner — flush under map */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white" data-testid="route-banner">
          <div className="px-5 py-3 overflow-hidden">
            <div className="flex items-center min-w-0">
              {/* Departure */}
              <div className="flex-shrink-0 max-w-[160px]" title={operation.departure}>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-white flex-shrink-0" />
                  <span className="text-[10px] text-white/60 uppercase tracking-wide">Départ</span>
                </div>
                <p className="font-semibold text-sm mt-0.5 pl-4 truncate">{depCity}</p>
              </div>

              {/* Connector */}
              <div className="flex-1 mx-2 border-t border-dashed border-white/30 min-w-[12px]" />

              {/* Intermediate Stops */}
              {hasStops && stops.map((stop, idx) => (
                <React.Fragment key={idx}>
                  <div className="flex-shrink-0 max-w-[140px] text-center" title={stop.address || stop.city || ''}>
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-blue-300 flex-shrink-0" />
                      <span className="text-[10px] text-white/60">{stop.order || idx + 1}</span>
                    </div>
                    <p className="font-medium text-sm mt-0.5 truncate">{shortName(stop.address || stop.city)}</p>
                  </div>
                  <div className="flex-1 mx-2 border-t border-dashed border-white/30 min-w-[12px]" />
                </React.Fragment>
              ))}

              {/* Distance */}
              {operation.distance_km ? (
                <>
                  <div className="flex-shrink-0 text-center px-1">
                    <p className="text-lg font-bold leading-tight">{Math.round(operation.distance_km).toLocaleString('fr-FR')}</p>
                    <p className="text-[10px] text-white/50 uppercase tracking-wider">km</p>
                  </div>
                  <div className="flex-1 mx-2 border-t border-dashed border-white/30 min-w-[12px]" />
                </>
              ) : null}

              {/* Arrival */}
              <div className="flex-shrink-0 max-w-[160px] text-right" title={operation.arrival}>
                <div className="flex items-center justify-end gap-1.5">
                  <span className="text-[10px] text-white/60 uppercase tracking-wide">Arrivée</span>
                  <div className="w-2.5 h-2.5 rounded-full bg-red-400 flex-shrink-0" />
                </div>
                <p className="font-semibold text-sm mt-0.5 pr-4 truncate">{arrCity}</p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Operation Details */}
      <Card className="p-4">
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Package className="w-4 h-4 text-gray-600" />
            <h3 className="text-sm font-semibold text-gray-900">Détails de l'Opération</h3>
            {hasStops && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium" data-testid="multi-stop-badge">
                +{stops.length} étape{stops.length > 1 ? 's' : ''}
              </span>
            )}
            {operation.tripType === 'round_trip' && (
              <span
                className="text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 bg-gray-100 text-gray-700 border border-gray-200"
                data-testid="round-trip-badge"
                title={
                  operation.returnCargoMode === 'empty'
                    ? 'Retour à vide — optimisation possible'
                    : operation.returnCargoMode === 'different'
                    ? 'Aller-retour avec marchandise différente au retour'
                    : 'Aller-retour — même marchandise'
                }
              >
                <RotateCcw className="w-3 h-3" />
                Aller-retour
                {operation.returnCargoMode === 'empty' && <span className="text-[10px] text-gray-500">· retour vide</span>}
              </span>
            )}
          </div>

          {/* Route detail green box */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-3" data-testid="route-detail-box">
            <div className="flex items-center gap-3">
              {/* Departure */}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Départ</p>
                <p className="font-semibold text-sm text-gray-900 truncate" title={operation.departure}>{operation.departure}</p>
              </div>

              {!hasStops && (
                <div className="flex items-center justify-center flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center">
                    <span className="text-white text-lg">→</span>
                  </div>
                </div>
              )}

              {/* Arrival (when no stops) */}
              {!hasStops && (
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide">Arrivée</p>
                  <p className="font-semibold text-sm text-gray-900 truncate" title={operation.arrival}>{operation.arrival}</p>
                </div>
              )}
            </div>

            {/* Stops timeline (when multi-stop) */}
            {hasStops && (
              <div className="mt-3 pt-3 border-t border-emerald-200 space-y-2.5" data-testid="stops-detail-list">
                {stops.map((stop, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="flex flex-col items-center flex-shrink-0 mt-0.5">
                      <div className="w-5 h-5 rounded-full bg-blue-100 border-2 border-blue-400 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-blue-600">{stop.order || idx + 1}</span>
                      </div>
                      {idx < stops.length - 1 && <div className="w-0.5 h-5 bg-blue-200 mt-0.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate" title={stop.address || stop.city}>
                        {stop.address || stop.city || 'Adresse non spécifiée'}
                      </p>
                      {stop.instructions && (
                        <p className="text-xs text-gray-500 italic truncate" title={stop.instructions}>{stop.instructions}</p>
                      )}
                    </div>
                  </div>
                ))}
                {/* Final arrival */}
                <div className="flex items-start gap-3">
                  <div className="flex flex-col items-center flex-shrink-0 mt-0.5">
                    <div className="w-5 h-5 rounded-full bg-red-100 border-2 border-red-400 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide">Arrivée</p>
                    <p className="text-sm font-medium text-gray-900 truncate" title={operation.arrival}>{operation.arrival}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Distance */}
            {operation.distance_km && (
              <div className="mt-2 pt-2 border-t border-emerald-200 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500">Distance totale :</span>
                <span className="text-sm font-bold text-emerald-700">{Math.round(operation.distance_km).toLocaleString('fr-FR')} km</span>
                {operation.tripType === 'round_trip' && operation.outboundDistance_km && (
                  <span className="text-[11px] text-gray-500">
                    ({Math.round(operation.outboundDistance_km)} km aller + {Math.round(operation.returnDistance_km || operation.outboundDistance_km)} km retour)
                  </span>
                )}
              </div>
            )}
          </div>

          {/* ✨ Card Trajet Retour (24/04/2026) — style sobre aligné sur le reste de l'app */}
          {operation.tripType === 'round_trip' && (
            <div
              className="rounded-lg p-3 border border-gray-200 bg-white"
              data-testid="return-leg-card"
            >
              <div className="flex items-center gap-2 mb-2">
                <RotateCcw className="w-3.5 h-3.5 text-gray-600" />
                <p className="text-xs font-semibold text-gray-900 uppercase tracking-wide">
                  Trajet retour
                </p>
                {operation.returnCargoMode === 'empty' && (
                  <span className="text-[10px] bg-gray-100 text-gray-600 border border-gray-200 px-1.5 py-0.5 rounded-full font-medium">
                    Non valorisé
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-gray-500 uppercase">Départ</p>
                  <p className="font-semibold text-sm text-gray-900 truncate" title={operation.arrival}>
                    {operation.arrival}
                  </p>
                </div>
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-100 border border-gray-200">
                  <ArrowRight className="w-3.5 h-3.5 text-gray-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-gray-500 uppercase">Arrivée</p>
                  <p className="font-semibold text-sm text-gray-900 truncate" title={operation.departure}>
                    {operation.departure}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100">
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">
                    {operation.operationType === 'transport_conteneur' ? 'Conteneur' : 'Marchandise'}
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {operation.returnCargoMode === 'empty'
                      ? 'Camion à vide'
                      : operation.returnCargoMode === 'same'
                      ? `Idem aller (${operation.operationType === 'transport_conteneur' && operation.containerType
                            ? formatContainerType(operation.containerType)
                            : formatMerchandiseType(operation.merchandiseType, operation.merchandiseTypeOther)})`
                      : (operation.operationType === 'transport_conteneur'
                            ? formatContainerType(operation.returnContainerType) || 'Non renseigné'
                            : formatMerchandiseType(operation.returnMerchandiseType, operation.returnMerchandiseTypeOther) || 'Non renseignée')}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-500 uppercase">Poids retour</p>
                  <p className="text-sm font-medium text-gray-800">
                    {operation.returnCargoMode === 'empty'
                      ? '0 T'
                      : `${operation.returnWeight || operation.weight || 0} T`}
                  </p>
                </div>
              </div>
              {operation.returnDepartureDate && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-[10px] text-gray-500 uppercase">Date départ retour</p>
                  <p className="text-sm font-medium text-gray-800">
                    {new Date(operation.returnDepartureDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {operation.externalRef && (
            <div
              className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center justify-between gap-3"
              data-testid="logistic-external-ref"
            >
              <div className="min-w-0">
                <p className="text-xs text-emerald-700 mb-0.5 flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  {operation.externalRefLabel || 'Référence client'}
                </p>
                <p className="font-semibold text-sm text-emerald-800 font-mono truncate" title={operation.externalRef}>
                  {operation.externalRef}
                </p>
              </div>
              {operation.externalRefSetBy && (
                <span className="text-[10px] text-emerald-700/70 whitespace-nowrap">
                  saisi par {operation.externalRefSetBy === 'staff' ? 'Baobab' : 'le client'}
                </span>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Véhicule</p>
              <div className="flex items-center gap-2">
                <Truck className="w-4 h-4 text-gray-400" />
                <p className="font-medium text-sm">{formatVehicleType(operation.vehicleType, operation.vehicleTypeOther)}</p>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Poids</p>
              <p className="font-semibold text-lg text-emerald-600">{operation.weight}T</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">Type d'opération</p>
              <p className="font-medium text-sm">{formatOperationType(operation.operationType, operation.operationTypeOther)}</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500 mb-1">
                {operation.operationType === 'transport_conteneur' && operation.containerType ? 'Conteneur' : 'Marchandise'}
              </p>
              <p className="font-medium text-sm">
                {operation.operationType === 'transport_conteneur' && operation.containerType
                  ? formatContainerType(operation.containerType)
                  : formatMerchandiseType(operation.merchandiseType, operation.merchandiseTypeOther) || '—'}
              </p>
            </div>

            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <p className="text-xs text-blue-600 mb-1 flex items-center gap-1">
                <Hash className="w-3 h-3" />
                Quantité de véhicules
              </p>
              <p className="font-semibold text-lg text-blue-700">
                {operation.numberOfTrucks || 1} {(operation.numberOfTrucks || 1) > 1 ? 'camions' : 'camion'}
              </p>
            </div>

            <div className={`rounded-lg p-3 border ${
              (operation.intraInterLand && String(operation.intraInterLand).toUpperCase() !== 'INTRALAND') ? 'bg-purple-50 border-purple-100' : 'bg-cyan-50 border-cyan-100'
            }`}>
              <p className={`text-xs mb-1 flex items-center gap-1 ${
                (operation.intraInterLand && String(operation.intraInterLand).toUpperCase() !== 'INTRALAND') ? 'text-purple-600' : 'text-cyan-600'
              }`}>
                <Globe className="w-3 h-3" />
                Classification
              </p>
              <p className={`font-semibold text-sm ${
                (operation.intraInterLand && String(operation.intraInterLand).toUpperCase() !== 'INTRALAND') ? 'text-purple-700' : 'text-cyan-700'
              }`}>
                {(operation.intraInterLand && String(operation.intraInterLand).toUpperCase() !== 'INTRALAND') ? 'HINTERLAND' : 'INTRALAND'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {(operation.intraInterLand && String(operation.intraInterLand).toUpperCase() !== 'INTRALAND') ? 'Transport hors Côte d\'Ivoire (régional / international)' : 'Transport interne (Côte d\'Ivoire)'}
              </p>
            </div>

            <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
              <p className="text-xs text-amber-600 mb-1 flex items-center gap-1">
                <Banknote className="w-3 h-3" />
                Valeur déclarée
              </p>
              <p className="font-semibold text-sm text-amber-700">
                {operation.merchandiseValue ? formatCurrency(operation.merchandiseValue) : 'Non déclarée'}
              </p>
            </div>

            <div className={`rounded-lg p-3 border ${operation.needsInsurance ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-200'}`}>
              <p className={`text-xs mb-1 flex items-center gap-1 ${operation.needsInsurance ? 'text-green-600' : 'text-gray-500'}`}>
                <Shield className="w-3 h-3" />
                Assurance
              </p>
              <p className={`font-semibold text-sm ${operation.needsInsurance ? 'text-green-700' : 'text-gray-600'}`}>
                {operation.needsInsurance ? '✓ Demandée' : '✗ Non demandée'}
              </p>
              {operation.needsInsurance && operation.insuranceRate && (
                <p className="text-xs text-green-600 mt-1">Taux: {operation.insuranceRate}%</p>
              )}
            </div>
          </div>

          {operation.needsInsurance && (operation.insuranceAmount || operation.merchandiseValue) && (
            <div className="bg-green-50 rounded-lg p-3 border border-green-200 mt-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  <p className="text-sm font-semibold text-green-700">Détail Assurance</p>
                </div>
                {operation.insuranceAmount && (
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Montant</p>
                    <p className="font-semibold text-green-700">{formatCurrency(operation.insuranceAmount)}</p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-green-200 text-xs">
                <div>
                  <p className="text-gray-500">Valeur assurée</p>
                  <p className="font-medium text-gray-700">{formatCurrency(operation.merchandiseValue)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Taux appliqué</p>
                  <p className="font-medium text-gray-700">{operation.insuranceRate || 0}%</p>
                </div>
                <div>
                  <p className="text-gray-500">Zone</p>
                  <p className="font-medium text-gray-700">{operation.intraInterLand || 'INTRALAND'}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-3 border-t text-xs text-gray-600">
            <div className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              <span>Livraison: {formatDate(operation.deliveryDate)}</span>
            </div>
            <div className="flex items-center gap-2">
              {operation.createdByName && (
                <span className="text-gray-400" data-testid="op-created-by">par {operation.createdByName}</span>
              )}
              <span>Créé: {formatDate(operation.createdAt)}</span>
            </div>
          </div>

          {operation.description && (
            <div className="pt-3 border-t">
              <p className="text-xs text-gray-500 mb-1">Description</p>
              <p className="text-sm text-gray-700 leading-relaxed">{operation.description}</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default LogisticBlock;
